import React, { useCallback, useMemo } from 'react';
import { useWorkbook } from '@/contexts/workbook/WorkbookContext';
import { useWorkbookCountsUnified } from '@/hooks/content-generator/use-workbook-counts-unified';
import { ProjectFilterStatus } from '@/hooks/content-generator/workbook-types';
import { Button } from '@/components/ui/button';
import CountSkeleton from '@/components/ui/CountSkeleton';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { SidebarFilterButton } from '@/components/ui/SidebarFilterButtons';
import { 
  Grid3X3, 
  Archive, 
  Search,
  Tag,
  Filter,
  FileText,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import SidebarHeader from '@/components/ui/SidebarHeader';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import DashboardSearch from '@/components/ui/DashboardSearch';

const WorkbookSidebar = () => {
  const { 
    filterStatus, 
    setFilterStatus, 
    searchQuery,
    setSearchQuery,
    selectedTagId,
    setSelectedTagId,
    openCreateProjectModal
  } = useWorkbook();
  
  // Get unified counts and tag data from single efficient query
  const { data: counts, isLoading, error, workbooks } = useWorkbookCountsUnified();
  
  // Extract unique tags from workbooks data with counts
  const tags = useMemo(() => {
    if (!workbooks) return [];
    
    const tagMap = new Map();
    workbooks.forEach(workbook => {
      if (!workbook.is_archived) { // Only count tags from non-archived workbooks
        workbook.tags.forEach(tag => {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, {
              ...tag,
              count: counts?.tagCounts[tag.id] || 0
            });
          }
        });
      }
    });
    
    return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [workbooks, counts]);
  
  const isLoadingTags = isLoading;
  const tagsError = error;

  // Handle tag selection
  const handleTagClick = useCallback((tagId: string) => {
    // Clear status filter when selecting a tag
    if (filterStatus !== 'all') {
      setFilterStatus('all');
    }
    
    // If the same tag is clicked again, clear the filter
    if (selectedTagId === tagId) {
      setSelectedTagId(null);
    } else {
      setSelectedTagId(tagId);
    }
  }, [selectedTagId, setSelectedTagId, filterStatus, setFilterStatus]);

  // Handle status filter click
  const handleStatusClick = useCallback((status: ProjectFilterStatus) => {
    // Clear tag filter when selecting a status filter
    if (selectedTagId) {
      setSelectedTagId(null);
    }
    setFilterStatus(status);
  }, [selectedTagId, setSelectedTagId, setFilterStatus]);

  // Memoize filter options to prevent unnecessary re-renders
  const filterOptions = useMemo(() => [
    { key: 'all', label: 'All Projects', icon: Grid3X3, count: counts?.all || 0 },
  ] as const, [counts]);

  // Memoize callback functions
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);
  
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  // Get tag color variant mapping for consistent styling with main filters
  const getTagColorVariant = (tagColor: string) => {
    if (tagColor.includes('blue')) return 'blue';
    if (tagColor.includes('green')) return 'green';
    if (tagColor.includes('red')) return 'red';
    if (tagColor.includes('purple')) return 'purple';
    if (tagColor.includes('amber') || tagColor.includes('yellow')) return 'amber';
    if (tagColor.includes('orange')) return 'orange';
    if (tagColor.includes('indigo')) return 'indigo';
    return 'blue'; // Default fallback
  };

  // Get matching icon color class for tags (same style as main filters)
  const getTagIconColor = (tagColor: string) => {
    if (tagColor.includes('blue')) return 'text-blue-500';
    if (tagColor.includes('green')) return 'text-green-500';
    if (tagColor.includes('red')) return 'text-red-500';
    if (tagColor.includes('purple')) return 'text-purple-500';
    if (tagColor.includes('amber') || tagColor.includes('yellow')) return 'text-amber-500';
    if (tagColor.includes('orange')) return 'text-orange-500';
    if (tagColor.includes('indigo')) return 'text-indigo-500';
    return 'text-blue-500'; // Default fallback
  };

  const SidebarContent = () => (
    <div className="space-y-4 pt-2">
      {/* Search Field */}
      <div className="space-y-2">
        <div className="relative">
          <DashboardSearch
            placeholder="Search Projects"
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full"
          />
          {searchQuery && (
            <button 
              className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground hover:text-foreground"
              onClick={clearSearch}
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      {/* Unified Filter Options */}
      <div className="space-y-2">
        {/* Status Filters */}
        {filterOptions.map((option) => {
          const Icon = option.icon;
          const isActive = filterStatus === option.key && !selectedTagId;
          const variantMap = {
            all: 'purple',
          } as const;
          
          const iconColorMap = {
            all: 'text-purple-500',
          };
          
          return (
            <SidebarFilterButton
              key={option.key}
              variant={variantMap[option.key] || 'purple'}
              isActive={isActive}
              count={isLoading ? undefined : option.count}
              countElement={isLoading ? <CountSkeleton className="bg-gradient-to-r from-slate-200/80 to-slate-300/50 dark:from-slate-700/50 dark:to-slate-800/30" /> : undefined}
              onClick={() => handleStatusClick(option.key)}
            >
              <Icon size={16} className={iconColorMap[option.key]} />
              {option.label}
            </SidebarFilterButton>
          );
        })}

        {/* Category Filters - Styled exactly like main filters */}
        {tags && tags.length > 0 && tags.map((tag) => {
          const isActive = selectedTagId === tag.id;
          const variant = getTagColorVariant(tag.color);
          const iconColor = getTagIconColor(tag.color);
          
          return (
            <SidebarFilterButton
              key={tag.id}
              variant={variant}
              isActive={isActive}
              count={isLoading ? undefined : tag.count}
              countElement={isLoading ? <CountSkeleton className="bg-gradient-to-r from-slate-200/80 to-slate-300/50 dark:from-slate-700/50 dark:to-slate-800/30" /> : undefined}
              onClick={() => handleTagClick(tag.id)}
            >
              <Tag size={16} className={iconColor} />
              {tag.name}
            </SidebarFilterButton>
          );
        })}

        {/* Loading state for tags */}
        {isLoadingTags && (
          <>
            <CountSkeleton className="h-9 w-full rounded-md" />
            <CountSkeleton className="h-9 w-full rounded-md" />
          </>
        )}

        {/* Archived Filter */}
        <SidebarFilterButton
          variant="gray"
          isActive={filterStatus === 'archived' && !selectedTagId}
          count={isLoading ? undefined : counts?.archived || 0}
          countElement={isLoading ? <CountSkeleton className="bg-gradient-to-r from-slate-200/80 to-slate-300/50 dark:from-slate-700/50 dark:to-slate-800/30" /> : undefined}
          onClick={() => handleStatusClick('archived')}
        >
          <Archive size={16} className="text-gray-500" />
          Archived
        </SidebarFilterButton>
      </div>
    </div>
  );

  return (
    <div className="h-full">
      <MobileSidebarAccordion
        persistKey="workbook-sidebar"
        defaultExpanded={false}
        variant="purple"
        className="h-full"
      >
        <SidebarHeader title="Search & Filter" icon={FileText} variant="purple" />
        <SidebarContent />
      </MobileSidebarAccordion>
    </div>
  );
};

export default WorkbookSidebar;
